import React from 'react';
import { createPortal } from 'react-dom';
import './NavFlyout.css';

interface NavFlyoutProps {
    /** Section label shown as the flyout header. */
    title: string;
    /** Bounding rect of the rail icon the flyout anchors to. */
    anchorRect: DOMRect;
    /** The pre-rendered nav-item <li> nodes (from the shared renderNavLink helper). */
    children: React.ReactNode;
    /** Cancel the pending close timer (cursor entered the panel). */
    onMouseEnter: () => void;
    /** Start the close timer (cursor left the panel). */
    onMouseLeave: () => void;
}

const VIEWPORT_MARGIN = 8;
/** Minimum assumed panel height (px) used in the viewport top-clamp so the panel
 *  top never pushes the body fully below the visible area. The list's max-height
 *  provides the complementary scroll when actual content exceeds the remaining space. */
const MIN_VISIBLE_HEIGHT = 120;

/**
 * Floating glassmorphism panel revealing a section's items next to the collapsed
 * sidebar rail (HubSpot-style). Portaled to document.body so it escapes the
 * sidebar's `overflow: hidden` and the `.sidebar-collapsed .text { display:none }`
 * descendant rules. Positioned `fixed` against the anchor icon, clamped to the
 * viewport with the item list scrolling when the section is tall.
 */
const NavFlyout: React.FC<NavFlyoutProps> = ({ title, anchorRect, children, onMouseEnter, onMouseLeave }) => {
    const left = anchorRect.right;
    // Anchors in the lower half of the viewport (e.g. footer items, sections low
    // in the rail) open upward — the panel's bottom aligns to the anchor's bottom
    // and it grows up — so it never gets clamped/scrolled against the bottom edge.
    const openUpward = anchorRect.top > window.innerHeight / 2;
    const style: React.CSSProperties = openUpward
        ? { left, bottom: window.innerHeight - anchorRect.bottom, maxHeight: anchorRect.bottom - VIEWPORT_MARGIN }
        : (() => {
            const top = Math.max(
                VIEWPORT_MARGIN,
                Math.min(anchorRect.top, window.innerHeight - VIEWPORT_MARGIN - MIN_VISIBLE_HEIGHT)
            );
            return { left, top, maxHeight: window.innerHeight - top - VIEWPORT_MARGIN };
        })();

    return createPortal(
        <div
            className="nav-flyout"
            style={style}
            onMouseEnter={onMouseEnter}
            onMouseLeave={onMouseLeave}
        >
            <div className="nav-flyout-title">{title}</div>
            <ul className="nav-flyout-list">
                {children}
            </ul>
        </div>,
        document.body
    );
};

export default NavFlyout;
